import { DeleteFilled, EditOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons'
import { dayjs } from '@hmedia/legenda-ds-common'
import { Button, Image, Popconfirm, Space, Table, Typography } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { type FC, useState } from 'react'
import { useQueryClient } from 'react-query'
import CustomContentEditorDrawerForm from './CustomContentEditorDrawerForm'
import ScreenAssignmentModal from './ScreenAssignmentModal'
import type { CustomContentAdminDTO } from './models'
import {
  useDeleteCustomContentMutation,
  useGetAllCustomContentsQuery,
} from './queries'

export const CUSTOMCONTENTVIEW_META = {
  path: '/custom-content',
  title: 'Egyedi tartalom',
}

const CustomContentView: FC = () => {
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isScreenSelectorOpen, setIsScreenSelectorOpen] = useState(false)
  const [editingContent, setEditingContent] = useState<CustomContentAdminDTO | undefined>(undefined)

  const getAllCustomContentsQuery = useGetAllCustomContentsQuery()
  const deleteCustomContentMutation = useDeleteCustomContentMutation(() => {
    queryClient.invalidateQueries('customContents')
  })
  const queryClient = useQueryClient()

  const sendToScreen = (content: CustomContentAdminDTO) => {
    setEditingContent(content)
    setIsScreenSelectorOpen(true)
  }

  const editContent = (content: CustomContentAdminDTO) => {
    setEditingContent(content)
    setIsEditorOpen(true)
  }

  const tableCols: ColumnType<CustomContentAdminDTO>[] = [
    {
      title: 'Elnevezés',
      key: 'name',
      dataIndex: 'name',
      align: 'left',
      width: '300px',
      render: (name: string) => <span>{name}</span>,
    },
    {
      title: 'Kép',
      key: 'imageUrl',
      dataIndex: 'imageUrl',
      align: 'left',
      width: '200px',
      render: (imageUrl: string) => (
        <Image
          src={imageUrl}
          alt="Custom content"
          height={40}
          style={{ objectFit: 'cover' }}
          preview={false}
        />
      ),
    },
    {
      title: 'Létrehozva',
      key: 'createdAt',
      dataIndex: 'createdAt',
      align: 'left',
      width: '150px',
      render: (createdAt: string) => (
        <span>{dayjs(createdAt).format('YYYY.MM.DD. HH:mm')}</span>
      ),
    },
    {
      title: 'Megjelenítés típusa',
      key: 'viewType',
      dataIndex: 'viewType',
      align: 'left',
      width: '200px',
      render: (viewType: string) => {
        const labels = {
          'GATE_INFO_HEADER': 'GATE típusú járatinformációs fejléccel',
          'GENERAL_INFO_HEADER': 'Általános járatinformációs fejléccel',
          'FULLSCREEN_IMAGE': 'Csak a képet mutassa teljes kijelzőn'
        }
        return <span>{labels[viewType as keyof typeof labels] || viewType}</span>
      },
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: '250px',
      render: (content: CustomContentAdminDTO) => (
        <>
          <Space direction="horizontal">
            <Button
              type="primary"
              icon={<PictureOutlined />}
              onClick={() => sendToScreen(content)}
            >
              Képernyőre
            </Button>
            <Button type="default" icon={<EditOutlined />} onClick={() => editContent(content)}>
              Szerkeszt
            </Button>
            <Popconfirm
              title="Biztos, hogy törlöd?"
              okText="Igen"
              cancelText="Nem"
              onConfirm={(e) =>
                deleteCustomContentMutation.mutate(content.id)
              }
            >
              <Button type="link" icon={<DeleteFilled />} />
            </Popconfirm>
          </Space>
        </>
      ),
    },
  ]

  const closeCancelEditing = () => {
    setIsEditorOpen(false)
    setEditingContent(undefined)
  }

  return (
    <div style={{ width: '100%' }}>
      <Space direction="horizontal" align="start" style={{ marginTop: '2rem', width: '100%' }}>
        <h1>Egyedi tartalmak</h1>
      </Space>
      <Space direction="vertical" style={{ width: '100%', marginTop: '2em' }} size={20}>
        <Space direction="horizontal" align="start" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsEditorOpen(true)}
            size={'large'}
          >
            Új tartalom hozzáadása
          </Button>
        </Space>
        <Table
          pagination={{ position: ['bottomCenter'] }}
          columns={tableCols}
          sortDirections={['ascend']}
          dataSource={getAllCustomContentsQuery.data}
          rowKey={'id'}
          loading={getAllCustomContentsQuery.isLoading}
        />
      </Space>
      {isEditorOpen && (
        <CustomContentEditorDrawerForm
          visible={isEditorOpen}
          content={editingContent}
          onCreate={() => {
            queryClient.invalidateQueries('customContents')
            closeCancelEditing()
          }}
          onClose={closeCancelEditing}
          onCancel={closeCancelEditing}
        />
      )}
      {isScreenSelectorOpen && (
        <ScreenAssignmentModal
          visible={isScreenSelectorOpen}
          content={editingContent}
          onClose={() => {
            setIsScreenSelectorOpen(false)
            setEditingContent(undefined)
            queryClient.invalidateQueries('customContents')
          }}
        />
      )}
    </div>
  )
}

export default CustomContentView